import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link2, RefreshCw, Save } from 'lucide-react';
import { PageHeader } from '@components/common/PageHeader';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { SectionCard } from '@components/common/SectionCard';
import {
  useApolloEmailAccounts,
  useApolloConnectionTest,
  useApolloSequences,
  useApolloUsers,
  useCRMSettings,
  useUpdateCRMSettings,
} from '@hooks/useCRM';
import { useRefreshStripeConnection, useStartStripeConnection, useStripeConnection } from '@hooks/usePayments';
import { formatDate } from '@lib/format';
import type { CRMSettingUpdatePayload } from '@/types/api';

type SettingsFormState = {
  crm_display_name: string;
  sender_name: string;
  sender_email: string;
  sender_phone: string;
  default_sequence_id: string;
  apollo_enabled: boolean;
  apollo_api_base_url: string;
  apollo_webhook_secret: string;
  apollo_default_sequence_id: string;
  apollo_default_email_account_id: string;
  apollo_default_user_id: string;
};

const DEFAULT_APOLLO_BASE_URL = 'https://api.apollo.io/api/v1';

const EMPTY_FORM: SettingsFormState = {
  crm_display_name: '',
  sender_name: '',
  sender_email: '',
  sender_phone: '',
  default_sequence_id: '',
  apollo_enabled: false,
  apollo_api_base_url: DEFAULT_APOLLO_BASE_URL,
  apollo_webhook_secret: '',
  apollo_default_sequence_id: '',
  apollo_default_email_account_id: '',
  apollo_default_user_id: '',
};

function buildFormState(settings?: {
  crm_display_name?: string | null;
  sender_name?: string | null;
  sender_email?: string | null;
  sender_phone?: string | null;
    default_sequence_id?: string | null;
    apollo?: {
      enabled: boolean;
      api_base_url: string;
      has_api_key?: boolean;
      api_key_last4?: string | null;
      default_sequence_id?: string | null;
      has_webhook_secret?: boolean;
      webhook_secret_last4?: string | null;
      default_email_account_id?: string | null;
      default_user_id?: string | null;
  };
}): SettingsFormState {
  return {
    crm_display_name: settings?.crm_display_name ?? '',
    sender_name: settings?.sender_name ?? '',
    sender_email: settings?.sender_email ?? '',
    sender_phone: settings?.sender_phone ?? '',
    default_sequence_id: settings?.default_sequence_id ?? '',
    apollo_enabled: settings?.apollo?.enabled ?? false,
    apollo_api_base_url: settings?.apollo?.api_base_url ?? DEFAULT_APOLLO_BASE_URL,
    apollo_webhook_secret: '',
    apollo_default_sequence_id: settings?.apollo?.default_sequence_id ?? '',
    apollo_default_email_account_id: settings?.apollo?.default_email_account_id ?? '',
    apollo_default_user_id: settings?.apollo?.default_user_id ?? '',
  };
}

function normalizeOptional(value: string) {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function SettingsPage() {
  const { data, isLoading } = useCRMSettings();
  const { data: stripeConnection } = useStripeConnection();
  const startStripeConnection = useStartStripeConnection();
  const refreshStripeConnection = useRefreshStripeConnection();
  const updateSettingsMutation = useUpdateCRMSettings();
  const testApolloMutation = useApolloConnectionTest();
  const [formState, setFormState] = useState<SettingsFormState>(EMPTY_FORM);
  const [clearApolloWebhookSecret, setClearApolloWebhookSecret] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const hasConfiguredApolloKey = Boolean(data?.apollo.has_api_key);
  const hasSavedApolloWebhookSecret = Boolean(data?.apollo.has_webhook_secret);
  const canLoadApolloMetadata = Boolean(data?.apollo.enabled && data?.apollo.has_api_key);
  const canLoadApolloSequences = canLoadApolloMetadata;
  const { data: apolloSequences, isLoading: isApolloSequencesLoading } = useApolloSequences(undefined, canLoadApolloSequences);
  const { data: apolloEmailAccounts, isLoading: isApolloEmailAccountsLoading } =
    useApolloEmailAccounts(canLoadApolloMetadata);
  const { data: apolloUsers, isLoading: isApolloUsersLoading } = useApolloUsers(canLoadApolloMetadata);

  useEffect(() => {
    if (!data) {
      return;
    }
    setFormState(buildFormState(data));
    setClearApolloWebhookSecret(false);
  }, [data]);

  const sequenceOptions = useMemo(() => {
    const items = apolloSequences ? [...apolloSequences] : [];
    if (
      formState.apollo_default_sequence_id &&
      !items.some((sequence) => sequence.id === formState.apollo_default_sequence_id)
    ) {
      items.unshift({
        id: formState.apollo_default_sequence_id,
        name: formState.apollo_default_sequence_id,
      });
    }
    return items;
  }, [apolloSequences, formState.apollo_default_sequence_id]);

  const emailAccountOptions = useMemo(() => {
    const items = apolloEmailAccounts ? [...apolloEmailAccounts] : [];
    if (
      formState.apollo_default_email_account_id &&
      !items.some((account) => account.id === formState.apollo_default_email_account_id)
    ) {
      items.unshift({
        id: formState.apollo_default_email_account_id,
        email: formState.apollo_default_email_account_id,
      });
    }
    return items;
  }, [apolloEmailAccounts, formState.apollo_default_email_account_id]);

  const apolloUserOptions = useMemo(() => {
    const items = apolloUsers ? [...apolloUsers] : [];
    if (formState.apollo_default_user_id && !items.some((user) => user.id === formState.apollo_default_user_id)) {
      items.unshift({
        id: formState.apollo_default_user_id,
        name: formState.apollo_default_user_id,
      });
    }
    return items;
  }, [apolloUsers, formState.apollo_default_user_id]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaveMessage(null);

    const payload: CRMSettingUpdatePayload = {
      crm_display_name: normalizeOptional(formState.crm_display_name),
      sender_name: normalizeOptional(formState.sender_name),
      sender_email: normalizeOptional(formState.sender_email),
      sender_phone: normalizeOptional(formState.sender_phone),
      default_sequence_id: normalizeOptional(formState.default_sequence_id),
      apollo: {
        enabled: formState.apollo_enabled,
        api_base_url: normalizeOptional(formState.apollo_api_base_url) ?? DEFAULT_APOLLO_BASE_URL,
        default_sequence_id: normalizeOptional(formState.apollo_default_sequence_id),
        default_email_account_id: normalizeOptional(formState.apollo_default_email_account_id),
        default_user_id: normalizeOptional(formState.apollo_default_user_id),
      },
    };

    const webhookSecret = formState.apollo_webhook_secret.trim();
    if (clearApolloWebhookSecret) {
      payload.apollo!.webhook_secret = '';
    } else if (webhookSecret) {
      payload.apollo!.webhook_secret = webhookSecret;
    }

    await updateSettingsMutation.mutateAsync(payload);
    setSaveMessage('Settings saved.');
    setFormState((current) => ({ ...current, apollo_webhook_secret: '' }));
    setClearApolloWebhookSecret(false);
  }

  async function handleTestApollo() {
    const payload = {
      api_base_url: normalizeOptional(formState.apollo_api_base_url) ?? DEFAULT_APOLLO_BASE_URL,
    };
    await testApolloMutation.mutateAsync(payload);
  }

  async function handleStartStripe() {
    const connection = await startStripeConnection.mutateAsync({
      return_path: '/settings',
      force_reconnect: stripeConnection?.status === 'ready',
    });
    if (connection.onboarding_url) {
      window.location.assign(connection.onboarding_url);
    }
  }

  return (
    <div className="detail-stack">
      <PageHeader
        title="Internal CRM Settings"
        subtitle="Configure sender identity and the internal Apollo integration that will power outbound sequence execution."
      />

      <form className="detail-stack" onSubmit={handleSubmit}>
        <SectionCard title="CRM Identity" subtitle="Founder-facing branding and defaults used inside the dedicated CRM shell.">
          {isLoading ? (
            <div className="loading-row">Loading settings…</div>
          ) : (
            <div className="detail-form">
              <div className="form-grid">
                <label className="form-field">
                  <span>CRM Name</span>
                  <input
                    value={formState.crm_display_name}
                    onChange={(event) => setFormState((current) => ({ ...current, crm_display_name: event.target.value }))}
                    placeholder="EZ Flow CRM"
                  />
                </label>
                <label className="form-field">
                  <span>Sender Name</span>
                  <input
                    value={formState.sender_name}
                    onChange={(event) => setFormState((current) => ({ ...current, sender_name: event.target.value }))}
                    placeholder="Founder"
                  />
                </label>
                <label className="form-field">
                  <span>Sender Email</span>
                  <input
                    type="email"
                    value={formState.sender_email}
                    onChange={(event) => setFormState((current) => ({ ...current, sender_email: event.target.value }))}
                    placeholder="founder@ezflow.ai"
                  />
                </label>
                <label className="form-field">
                  <span>Sender Phone</span>
                  <input
                    value={formState.sender_phone}
                    onChange={(event) => setFormState((current) => ({ ...current, sender_phone: event.target.value }))}
                    placeholder="+1 555 010 100"
                  />
                </label>
                <label className="form-field form-field-wide">
                  <span>Default CRM Sequence ID</span>
                  <input
                    value={formState.default_sequence_id}
                    onChange={(event) => setFormState((current) => ({ ...current, default_sequence_id: event.target.value }))}
                    placeholder="Optional internal CRM sequence ID"
                  />
                </label>
              </div>
            </div>
          )}
        </SectionCard>

        <SectionCard title="Stripe Payments" subtitle="Tenant-level Stripe connection used for AP payouts across the platform.">
          <div className="detail-form">
            <div className="integration-status-row">
              <span className={`pill ${stripeConnection?.status === 'ready' ? 'pill-medium' : 'pill-soft'}`}>
                {(stripeConnection?.status || 'not_started').replace(/_/g, ' ')}
              </span>
              <span className="muted-copy">
                {stripeConnection?.mode === 'stub'
                  ? 'Local stub mode is active until Stripe credentials are configured in the backend.'
                  : stripeConnection?.account_id
                  ? `Connected account ${stripeConnection.account_id}`
                  : 'No Stripe account connected yet.'}
              </span>
            </div>
            {stripeConnection?.requirements_currently_due?.length ? (
              <p className="helper-copy">
                Requirements due: {stripeConnection.requirements_currently_due.join(', ')}
              </p>
            ) : null}
            <div className="button-row">
              <button type="button" className="secondary-button" onClick={() => refreshStripeConnection.mutateAsync()}>
                Refresh
              </button>
              <button type="button" className="primary-button" onClick={handleStartStripe}>
                {startStripeConnection.isPending ? 'Starting…' : stripeConnection?.status === 'ready' ? 'Reconnect Stripe' : 'Connect Stripe'}
              </button>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Apollo Integration" subtitle="Internal env-backed connection used for Apollo sequence execution, enrichment, and future cadence sync.">
          {isLoading ? (
            <div className="loading-row">Loading Apollo integration…</div>
          ) : (
            <div className="detail-form">
              <div className="integration-status-row">
                <span className={`pill ${formState.apollo_enabled ? 'pill-medium' : 'pill-soft'}`}>
                  {formState.apollo_enabled ? 'Enabled' : 'Disabled'}
                </span>
                <span className="muted-copy">
                  {hasConfiguredApolloKey
                    ? `Apollo API key configured${data?.apollo.api_key_last4 ? ` (ending in ${data.apollo.api_key_last4})` : ''}`
                    : 'Set APOLLO_API_KEY in the backend environment'}
                </span>
              </div>

              <div className="form-grid">
                <label className="form-field checkbox-field">
                  <span>Enable Apollo</span>
                  <input
                    type="checkbox"
                    checked={formState.apollo_enabled}
                    onChange={(event) => setFormState((current) => ({ ...current, apollo_enabled: event.target.checked }))}
                  />
                </label>

                <label className="form-field">
                  <span>Apollo API Base URL</span>
                  <input
                    value={formState.apollo_api_base_url}
                    onChange={(event) => setFormState((current) => ({ ...current, apollo_api_base_url: event.target.value }))}
                    placeholder={DEFAULT_APOLLO_BASE_URL}
                  />
                </label>

                <label className="form-field form-field-wide">
                  <span>Apollo Event Secret</span>
                  <input
                    type="password"
                    value={formState.apollo_webhook_secret}
                    onChange={(event) => {
                      setClearApolloWebhookSecret(false);
                      setFormState((current) => ({ ...current, apollo_webhook_secret: event.target.value }));
                    }}
                    placeholder={
                      hasSavedApolloWebhookSecret && !clearApolloWebhookSecret
                        ? 'Leave blank to keep the saved event secret'
                        : 'Shared secret for inbound Apollo event relay'
                    }
                  />
                </label>

                <label className="form-field form-field-wide">
                  <span>Default Apollo Sequence</span>
                  <SearchableSelect
                    options={[
                      {
                        id: '',
                        label: canLoadApolloSequences ? 'No default Apollo sequence' : 'Set APOLLO_API_KEY to load sequences',
                      },
                      ...sequenceOptions.map((sequence) => ({
                        id: sequence.id,
                        label: sequence.name,
                        description: [sequence.status, sequence.owner_name].filter(Boolean).join(' • '),
                      })),
                    ]}
                    value={formState.apollo_default_sequence_id}
                    onChange={(value) => setFormState((current) => ({ ...current, apollo_default_sequence_id: value }))}
                    disabled={!canLoadApolloSequences}
                    isLoading={isApolloSequencesLoading}
                    placeholder="Default Apollo sequence"
                    clearable={false}
                  />
                </label>

                <label className="form-field">
                  <span>Default Apollo Email Account</span>
                  <SearchableSelect
                    options={[
                      {
                        id: '',
                        label: canLoadApolloMetadata ? 'No default Apollo email account' : 'Set APOLLO_API_KEY to load email accounts',
                      },
                      ...emailAccountOptions.map((account) => ({
                        id: account.id,
                        label: account.email,
                        description: [account.sender_name, account.status].filter(Boolean).join(' • '),
                      })),
                    ]}
                    value={formState.apollo_default_email_account_id}
                    onChange={(value) => setFormState((current) => ({ ...current, apollo_default_email_account_id: value }))}
                    disabled={!canLoadApolloMetadata}
                    isLoading={isApolloEmailAccountsLoading}
                    placeholder="Default Apollo email account"
                    clearable={false}
                  />
                </label>

                <label className="form-field">
                  <span>Default Apollo User</span>
                  <SearchableSelect
                    options={[
                      {
                        id: '',
                        label: canLoadApolloMetadata ? 'No default Apollo user' : 'Set APOLLO_API_KEY to load Apollo users',
                      },
                      ...apolloUserOptions.map((user) => ({
                        id: user.id,
                        label: user.name,
                        description: user.email ?? undefined,
                      })),
                    ]}
                    value={formState.apollo_default_user_id}
                    onChange={(value) => setFormState((current) => ({ ...current, apollo_default_user_id: value }))}
                    disabled={!canLoadApolloMetadata}
                    isLoading={isApolloUsersLoading}
                    placeholder="Default Apollo user"
                    clearable={false}
                  />
                </label>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => setClearApolloWebhookSecret((current) => !current)}
                  disabled={!hasSavedApolloWebhookSecret && !clearApolloWebhookSecret}
                >
                  <RefreshCw size={16} />
                  {clearApolloWebhookSecret ? 'Keep Event Secret' : 'Remove Event Secret'}
                </button>
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => void handleTestApollo()}
                  disabled={testApolloMutation.isPending || !hasConfiguredApolloKey}
                >
                  <Link2 size={16} />
                  {testApolloMutation.isPending ? 'Testing…' : 'Test Apollo Connection'}
                </button>
                <button type="submit" className="button button-primary" disabled={updateSettingsMutation.isPending}>
                  <Save size={16} />
                  {updateSettingsMutation.isPending ? 'Saving…' : 'Save Settings'}
                </button>
              </div>

              {clearApolloWebhookSecret ? (
                <p className="form-error">Saving now will remove the stored Apollo event secret.</p>
              ) : null}

              {testApolloMutation.data ? (
                <p className="success-copy">
                  {testApolloMutation.data.message}
                  {testApolloMutation.data.sequence_count != null ? ` Sequence preview returned ${testApolloMutation.data.sequence_count} row(s).` : ''}
                </p>
              ) : null}

              {testApolloMutation.error ? (
                <p className="form-error">
                  {testApolloMutation.error instanceof Error ? testApolloMutation.error.message : 'Apollo connection test failed.'}
                </p>
              ) : null}

              {saveMessage ? <p className="success-copy">{saveMessage}</p> : null}

              <div className="integration-meta-grid">
                <div>
                  <dt>API Key</dt>
                  <dd>{hasConfiguredApolloKey ? `Configured${data?.apollo.api_key_last4 ? ` • ••••${data.apollo.api_key_last4}` : ''}` : 'Not configured'}</dd>
                </div>
                <div>
                  <dt>Event Secret</dt>
                  <dd>
                    {hasSavedApolloWebhookSecret && !clearApolloWebhookSecret
                      ? `••••${data?.apollo.webhook_secret_last4 ?? ''}`
                      : 'Not stored'}
                  </dd>
                </div>
                <div>
                  <dt>Available Sequences</dt>
                  <dd>{canLoadApolloSequences ? (isApolloSequencesLoading ? 'Loading…' : `${sequenceOptions.length}`) : 'Unavailable'}</dd>
                </div>
                <div>
                  <dt>Email Accounts</dt>
                  <dd>{canLoadApolloMetadata ? (isApolloEmailAccountsLoading ? 'Loading…' : `${emailAccountOptions.length}`) : 'Unavailable'}</dd>
                </div>
                <div>
                  <dt>Apollo Users</dt>
                  <dd>{canLoadApolloMetadata ? (isApolloUsersLoading ? 'Loading…' : `${apolloUserOptions.length}`) : 'Unavailable'}</dd>
                </div>
                <div>
                  <dt>Last Test</dt>
                  <dd>{testApolloMutation.data ? formatDate(testApolloMutation.data.tested_at) : 'Not run this session'}</dd>
                </div>
              </div>
            </div>
          )}
        </SectionCard>
      </form>
    </div>
  );
}
